import { useState, type FormEvent } from 'react'
import { del, post, put } from '../../../api'
import { Alert, Button, Card, Field, Input, Modal, Toggle } from '../../../components/ui'
import { useAction } from '../../../hooks'
import type { Area } from '../../../types'
import { useBusiness } from '../Layout'

export function AreasSection() {
  const { business, reload, can } = useBusiness()
  const [editing, setEditing] = useState<Area | 'new' | null>(null)
  const remove = useAction(async (a: Area) => {
    if (!confirm(`Retire ${a.name}? Its bookings are kept; it will no longer be offered.`)) return
    await del(`/v1/admin/businesses/${business.id}/areas/${a.id}`)
    await reload({ quiet: true })
  })
  const total = business.areas.reduce((s, a) => s + a.capacityCovers, 0)
  const editable = can('manager')
  return (
    <div className="stack-lg">
      <Card
        title={`Areas · ${total} covers`}
        actions={
          editable ? (
            <Button size="sm" onClick={() => setEditing('new')}>
              + Add area
            </Button>
          ) : null
        }
      >
        {remove.error ? <Alert>{remove.error}</Alert> : null}
        <ul className="list-plain">
          {business.areas.map((a) => (
            <li key={a.id}>
              <div>
                <div style={{ fontWeight: 600 }}>{a.name}</div>
                <div className="muted small">
                  {a.capacityCovers} covers · {a.bookableOnline ? 'bookable online' : 'staff bookings only'}
                </div>
              </div>
              {editable ? (
                <div className="row-flex">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void remove.run(a)} disabled={business.areas.length === 1}>
                    Retire
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        <p className="hint">Capacity is in covers (seats), not tables. Guests choose an area only when more than one is bookable online.</p>
      </Card>
      {editing ? <AreaModal area={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void reload({ quiet: true }))} /> : null}
    </div>
  )
}

function AreaModal({ area, onClose, onSaved }: { area?: Area; onClose: () => void; onSaved: () => void }) {
  const { business } = useBusiness()
  const [name, setName] = useState(area?.name ?? '')
  const [capacity, setCapacity] = useState(area?.capacityCovers ?? 20)
  const [online, setOnline] = useState(area?.bookableOnline ?? true)
  const save = useAction(async () => {
    const body = { name: name.trim(), capacityCovers: capacity, bookableOnline: online }
    if (area) await put(`/v1/admin/businesses/${business.id}/areas/${area.id}`, body)
    else await post(`/v1/admin/businesses/${business.id}/areas`, body)
    onSaved()
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void save.run()
  }
  return (
    <Modal
      title={area ? `Edit ${area.name}` : 'Add an area'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="area-form" busy={save.busy}>
            Save
          </Button>
        </>
      }
    >
      <form id="area-form" onSubmit={submit} className="stack">
        {save.error ? <Alert>{save.error}</Alert> : null}
        <Field label="Name">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} required />}</Field>
        <Field label="Covers">{(id) => <Input id={id} type="number" min={0} max={2000} value={capacity} onChange={(e) => setCapacity(Number(e.target.value))} required />}</Field>
        <Toggle checked={online} onChange={setOnline} label="Bookable online" description="Off for areas you only seat by hand, like a private room." />
      </form>
    </Modal>
  )
}
